import { X } from 'lucide-react';
import CompaniesBenefitPointers from './CompaniesBenefitPointers';
import type { SearchSuggestion } from '../../favorites-app/types';

interface SelectedFavoriteChipsProps {
  selected: SearchSuggestion[];
  isLoadingFavorites?: boolean;
  onRemove: (tickerSymbol: string) => void;
}

function FavoriteChipsGrid({
  selected,
  onRemove,
}: {
  selected: SearchSuggestion[];
  onRemove: (tickerSymbol: string) => void;
}) {
  return (
    <div
      className="grid grid-cols-3 gap-x-1 gap-y-3.5 sm:grid-cols-6 sm:gap-x-3 sm:gap-y-3"
      role="list"
    >
      {selected.map((stock) => {
        const initials = stock.ticker_symbol.slice(0, 2).toUpperCase();

        return (
          <div
            key={stock.ticker_symbol}
            className="flex w-full min-w-0 items-center rounded-full border border-slate-300 bg-background-dark py-1 pl-1.5 pr-0.5 shadow-[0_1px_2px_0_rgba(15,23,42,0.04)] transition-[border-color,box-shadow] duration-150 hover:border-slate-400 hover:shadow-[0_1px_4px_0_rgba(15,23,42,0.06)] sm:py-1.5 sm:pl-2.5 sm:pr-1"
            role="listitem"
          >
            <div className="mr-2 flex h-5 w-5 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-600 sm:mr-2">
              {stock.logo_url ? (
                <img src={stock.logo_url} alt="" className="h-3 w-3 object-contain" />
              ) : (
                <span className="text-[7px] font-semibold text-gray-300">{initials}</span>
              )}
            </div>

            <span className="min-w-0 flex-1 truncate text-xs font-semibold leading-none text-gray-900">
              {stock.ticker_symbol}
            </span>

            <button
              type="button"
              onClick={() => onRemove(stock.ticker_symbol)}
              className="ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-rose-400 sm:ml-1"
              aria-label={`Remove ${stock.ticker_symbol}`}
            >
              <X className="h-3 w-3" strokeWidth={4} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function SelectedFavoriteChips({
  selected,
  isLoadingFavorites = false,
  onRemove,
}: SelectedFavoriteChipsProps) {
  const isEmpty = selected.length === 0;

  return (
    <div className={`mx-auto flex w-full flex-col ${isEmpty ? 'sm:max-w-lg' : ''}`}>
      <div className="flex w-full flex-col rounded-xl border-[1.5px] border-dashed border-slate-300 bg-background">
        <div className="px-1.5 py-2 sm:px-3 sm:py-2.5">
          {isLoadingFavorites ? null : isEmpty ? (
            <CompaniesBenefitPointers />
          ) : (
            <FavoriteChipsGrid selected={selected} onRemove={onRemove} />
          )}
        </div>
      </div>
    </div>
  );
}
